import { defineStore } from 'pinia'
import type { User } from '@/types'
import {
  sendOtpApi,
  loginApi,
  patchMeApi,
  type LoginWithCodeParams,
} from '@/datasources/api/auth'
import { clearSession, REFRESH_KEY, SESSION_USER_EVENT, TOKEN_KEY, USER_KEY } from '@/datasources/session'

/**
 * Cross-site login flag for the marketing site: a lightweight cookie scoped
 * to the registrable parent domain (e.g. .serverlessinsight.com) so
 * www.serverlessinsight.com can render 「控制台」 instead of 「登录」. Cookies
 * ignore ports, so localhost-based dev environments share it too. It carries
 * NO auth value — the console API still owns the real session.
 */
const AUTH_FLAG = 'si-auth=1'
const authCookieDomain = (): string => {
  const parts = location.hostname.split('.')
  // single-label hosts (localhost) take a host-only cookie; ports never matter
  return parts.length > 1 ? `.${parts.slice(-2).join('.')}` : location.hostname
}
const writeAuthFlag = (): void => {
  // 30d matches the refresh-token lifetime; the flag is cosmetic either way
  document.cookie = `${AUTH_FLAG}; domain=${authCookieDomain()}; path=/; max-age=${60 * 60 * 24 * 30}; SameSite=Lax`
}
const clearAuthFlag = (): void => {
  document.cookie = `si-auth=; domain=${authCookieDomain()}; path=/; max-age=0; SameSite=Lax`
}

/**
 * Decode the `exp` claim from a JWT (base64url payload, no verification —
 * the API enforces validity; here we only need to know whether the token is
 * still within its issued lifetime).
 */
const getTokenExpiry = (token: string): number | null => {
  const parts = token.split('.')
  if (parts.length < 2) return null
  try {
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')))
    return typeof payload.exp === 'number' ? payload.exp : null
  } catch {
    return null
  }
}

const isTokenUnexpired = (token: string): boolean => {
  const exp = getTokenExpiry(token)
  return exp !== null && exp * 1000 > Date.now()
}

interface AuthState {
  user: User | null
  loading: boolean
  error: string | null
  isNewUser: boolean
}

/**
 * B1: ONE mapping for every login path — dropping `roles` here hides the
 * platform-admin UI (BillingOpsTab keys off user.roles) even when the API
 * returned an admin. Keep the field set identical to the server's PublicUser.
 */
const mapAuthUser = (userSource: Record<string, unknown>): User => ({
  id: userSource['id'] as string,
  email: userSource['email'] as string,
  displayName: userSource['displayName'] as string,
  avatarUrl: userSource['avatarUrl'] as string | undefined,
  roles: userSource['roles'] as string[] | undefined,
  createdAt: userSource['createdAt'] as string | undefined,
})

// B1: a background token refresh may carry an updated user (e.g. platform
// admin granted server-side after the access token was minted) — sync the
// live store, not just localStorage.
window.addEventListener(
  SESSION_USER_EVENT,
  ((event: CustomEvent) => {
    const user = event.detail as User | null
    if (!user) return
    try {
      const store = useAuthStore()
      if (JSON.stringify(store.user) !== JSON.stringify(user)) {
        store.user = user
        localStorage.setItem(USER_KEY, JSON.stringify(user))
      }
    } catch {
      // pinia not active yet — hydration will pick the stored user up
    }
  }) as EventListener,
)

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: (() => {
      // Hydrate from localStorage on init
      try {
        const stored = localStorage.getItem(USER_KEY)
        return stored ? JSON.parse(stored) : null
      } catch {
        return null
      }
    })(),
    loading: false,
    error: null,
    isNewUser: false,
  }),
  getters: {
    // Auth is anchored on the REFRESH token: an expired access token is normal
    // (the apiClient auto-refreshes it on 401); only a missing/expired refresh
    // token means the session is over.
    isAuthenticated: (state) =>
      state.user !== null && isTokenUnexpired(localStorage.getItem(REFRESH_KEY) ?? ''),
  },
  actions: {
    async login(email: string, password: string): Promise<boolean> {
      this.loading = true
      this.error = null
      try {
        // Send OTP first
        await sendOtpApi(email)
        // Then login with OTP (password is actually the OTP code)
        const response = await loginApi({ email, code: password })
        const code = response.code as number
        if (code === 200 || code === 2000 || code === 2001) {
          const resData = response.data as unknown as Record<string, unknown>
          const resUser = resData['user'] as Record<string, unknown> | undefined
          const user = mapAuthUser(resUser ?? resData)
          const accessToken = (resData['accessToken'] ?? resData['access_token']) as string
          const refreshToken = (resData['refreshToken'] ?? resData['refresh_token']) as string
          this.user = user
          localStorage.setItem(TOKEN_KEY, accessToken)
          localStorage.setItem(REFRESH_KEY, refreshToken)
          localStorage.setItem(USER_KEY, JSON.stringify(user))
          writeAuthFlag()
          return true
        }
        if (code === 2002) {
          this.error = 'Consent required.'
          return false
        }
        const messages = response.messages
        this.error = Array.isArray(messages) ? messages[0] : 'Invalid OTP code.'
        return false
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Login failed.'
        return false
      } finally {
        this.loading = false
      }
    },

    async sendOtp(email: string): Promise<boolean> {
      this.loading = true
      this.error = null
      try {
        await sendOtpApi(email)
        return true
      } catch (err) {
        const messages = (err as { response?: { data?: { messages?: unknown } } }).response?.data
          ?.messages
        this.error = Array.isArray(messages)
          ? String(messages[0])
          : err instanceof Error
            ? err.message
            : 'Failed to send verification code.'
        return false
      } finally {
        this.loading = false
      }
    },

    async loginWithCode(
      params: LoginWithCodeParams,
    ): Promise<'success' | 'consent-required' | 'error'> {
      this.loading = true
      this.error = null
      try {
        const response = await loginApi(params)
        const code = response.code as number
        if (code === 200 || code === 2000 || code === 2001) {
          const resData = response.data as unknown as Record<string, unknown>
          const resUser = resData['user'] as Record<string, unknown> | undefined
          const user = mapAuthUser(resUser ?? resData)
          const accessToken = (resData['accessToken'] ?? resData['access_token']) as string
          const refreshToken = (resData['refreshToken'] ?? resData['refresh_token']) as string
          this.user = user
          localStorage.setItem(TOKEN_KEY, accessToken)
          localStorage.setItem(REFRESH_KEY, refreshToken)
          localStorage.setItem(USER_KEY, JSON.stringify(user))
          writeAuthFlag()
          this.isNewUser = resData['isNewUser'] === true
          return 'success'
        }
        if (code === 2002) {
          return 'consent-required'
        }
        const messages = response.messages
        const msg = Array.isArray(messages) ? messages[0] : messages
        this.error = msg ?? 'Invalid verification code.'
        return 'error'
      } catch (err) {
        this.error = err instanceof Error ? err.message : 'Login failed.'
        return 'error'
      } finally {
        this.loading = false
      }
    },

    async updateProfile(data: { displayName?: string; avatarUrl?: string }) {
      const res = await patchMeApi(data)
      if (res.data) {
        this.user = res.data
        localStorage.setItem(USER_KEY, JSON.stringify(res.data))
      }
    },

    logout() {
      this.user = null
      this.error = null
      this.isNewUser = false
      clearSession()
      clearAuthFlag()
    },
  },
})
